import { X, ThumbsUp, ThumbsDown, AlertTriangle, RotateCcw } from 'lucide-react';
import { useSubmitSourcingFeedback } from '../../hooks/useApi';
import { useToast } from '../../contexts/ToastContext';

interface SourcingCandidateDrawerProps {
  candidate: {
    candidateId: number;
    jobId: number;
    candidateName: string;
    candidateEmail: string;
    score: number;
    stars: number;
    aiPending: boolean;
    /**
     * Finding I2. `aiFailed` is a THIRD state, not the absence of `aiPending`:
     * the application is in `status='error'` with no review row, so nothing
     * ever assessed this person. Before this prop existed the drawer fell
     * through to its else-branch and rendered the cosine-similarity stars
     * under the label "AI Score" — presenting a retrieval score as an
     * assessment for a candidate no model had read.
     */
    aiFailed: boolean;
    /** Null when the match has no application row yet — then there is nothing to retry. */
    applicationId: number | null;
    aiReview: { overallScore: number; strengths: string[]; concerns: string[] } | null;
    feedback: 'approved' | 'rejected' | null;
  } | null;
  onClose: () => void;
  onFeedbackSubmit?: (candidateId: number, feedback: 'approved' | 'rejected') => void;
  /** Re-queues the failed AI screening. Owned by the page so the table and the drawer share one retried-set. */
  onRetryScreening?: (applicationId: number) => void;
  /** True once this application has been re-queued — the retry is only actionable once. */
  retryQueued?: boolean;
  retryPending?: boolean;
}

export default function SourcingCandidateDrawer({
  candidate,
  onClose,
  onFeedbackSubmit,
  onRetryScreening,
  retryQueued = false,
  retryPending = false,
}: SourcingCandidateDrawerProps) {
  const { mutateAsync, isPending } = useSubmitSourcingFeedback();
  const { showToast } = useToast();

  if (!candidate) return null;

  const handleFeedback = async (feedback: 'approved' | 'rejected') => {
    try {
      await mutateAsync({ jobId: candidate.jobId, candidateId: candidate.candidateId, feedback });
      onFeedbackSubmit?.(candidate.candidateId, feedback);
    } catch {
      showToast('Failed to submit feedback', 'error');
    }
  };

  return (
    <div
      data-testid="sourcing-candidate-drawer"
      className="fixed inset-y-0 right-0 z-40 w-full overflow-y-auto bg-white p-5 shadow-xl dark:bg-gray-800 sm:w-96"
    >
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {candidate.candidateName}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{candidate.candidateEmail}</p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Scores */}
      <div className="mb-5 space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        {candidate.aiFailed ? (
          <div className="space-y-3">
            {/* Resume Match is cosine similarity — a retrieval score, not an
                assessment — so it is still true when the AI never ran. The star
                rating labelled "AI Score" is NOT: it is derived from the same
                cosine number, and presenting it here claimed an assessment that
                does not exist. */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Resume Match</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(candidate.score * 100)}%
              </span>
            </div>
            <span
              data-testid="drawer-ai-failed-badge"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
            >
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              AI screening failed
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nothing assessed this candidate, so there is no rating to show.
            </p>
            {candidate.applicationId != null &&
              onRetryScreening &&
              (retryQueued ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">Re-queued</p>
              ) : (
                <button
                  type="button"
                  onClick={() => onRetryScreening(candidate.applicationId!)}
                  disabled={retryPending}
                  aria-label={`Retry AI screening for ${candidate.candidateName}`}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" />
                  Retry
                </button>
              ))}
          </div>
        ) : candidate.aiPending ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">AI screening in progress...</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Resume Match</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(candidate.score * 100)}%
              </span>
            </div>
            {/* US-507. This row used to render `candidate.stars` — the COSINE
                band, the same number shown as "Resume Match" one line above —
                under the label "AI Score". A recruiter hit a candidate whose
                review listed four missing required skills and showed five
                stars, and reasonably concluded the model had contradicted
                itself. It had not: it was never asked. The only number that may
                appear under an AI label is the one the model produced. */}
            {candidate.aiReview && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">AI Score</span>
                <span
                  data-testid="drawer-ai-score"
                  className="text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  {(candidate.aiReview.overallScore / 10).toFixed(1)}/5
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* AI review */}
      {candidate.aiReview && (
        <div className="mb-5 space-y-4">
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Strengths
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              {candidate.aiReview.strengths.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Concerns
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              {candidate.aiReview.concerns.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Feedback actions */}
      <div className="flex gap-3">
        <button
          onClick={() => handleFeedback('approved')}
          disabled={isPending}
          aria-label="Approve"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-green-500/15 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-500/25 disabled:opacity-40 dark:text-green-400"
        >
          <ThumbsUp className="h-4 w-4" /> Approve
        </button>
        <button
          onClick={() => handleFeedback('rejected')}
          disabled={isPending}
          aria-label="Reject"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-red-500/15 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-500/25 disabled:opacity-40 dark:text-red-400"
        >
          <ThumbsDown className="h-4 w-4" /> Reject
        </button>
      </div>
    </div>
  );
}
